import { cn } from "@gettactic/helpers";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon, MinusIcon } from "lucide-react";
import * as React from "react";
import { Label } from "../controls/Label/Label";

export type CheckboxProps = React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> & {
  /** `lg` (22px) for lists that are the main thing on screen, e.g. a sidebar. */
  size?: "default" | "lg";
};

const SIZE_CLASSES = {
  default: { box: "h-4 w-4 rounded-[4px]", icon: "h-4 w-4" },
  lg: { box: "h-5.5 w-5.5 rounded-[6px]", icon: "h-3.5 w-3.5 stroke-[3]" }
};

// purpose of this is to display hovered checkbox in storybook
export const checkboxHoverClassBase =
  "outline outline-[3px] outline-neutral-50";
const checkboxHoverClass = checkboxHoverClassBase
  .split(" ")
  .map((c) => `hover:${c} group-hover:${c}`)
  .join(" ");

/**
 * A checkbox with its label (Radix Checkbox): pass the label text as `children`. Use `checked` +
 * `onCheckedChange` to control it; `onCheckedChange` receives `true`, `false` or
 * `"indeterminate"`. Inside react-hook-form use `HookFormCheckboxInput`.
 */
const Checkbox = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, size = "default", ...props }, ref) => {
  // The label below needs an id to point at, or the checkbox has no accessible name
  const generatedId = React.useId();
  const id = props.id ?? generatedId;

  return (
    <div className="flex items-center">
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(
          "peer shrink-0",
          SIZE_CLASSES[size].box,
          "border border-neutral-400",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[state=checked]:border-none data-[state=checked]:bg-black data-[state=checked]:text-white",
          "data-[state=indeterminate]:border-none data-[state=indeterminate]:bg-black data-[state=indeterminate]:text-white",
          checkboxHoverClass,
          className
        )}
        {...props}
        id={id}
      >
        <CheckboxPrimitive.Indicator
          className={cn("flex items-center justify-center text-current")}
        >
          {props.checked === "indeterminate" ? (
            <MinusIcon className={SIZE_CLASSES[size].icon} />
          ) : (
            <CheckIcon className={SIZE_CLASSES[size].icon} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      <Label htmlFor={id} className="ml-2 text-normal">
        {props.children}
      </Label>
    </div>
  );
});

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
